import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "./api";

export const DEFAULT_IMAGE_MODEL = "openai/gpt-image-2.5-sunburst";

export type DesignModel = { id: string; name: string; description?: string; price?: string };

// Dropdown option etiketi: "Model Adı · $fiyat" (fiyat yoksa sadece ad)
export function modelLabel(m: { name: string; price?: string }): string {
  return m.price ? `${m.name} · ${m.price}` : m.name;
}

// Görsel üretim modeli seçimini localStorage'da hatırlar.
//
// Her ekran/modal KENDİ anahtarını verir: toplu üretimde ucuz model, tek
// tasarımda güçlü model gibi tercihler birbirini ezmesin. Anahtar verilmezse
// eski ortak anahtar kullanılır (geriye dönük uyum).
//
// İlk açılışta ekranın kendi kaydı yoksa varsayılan GPT Image 2.5 Sunburst.
export function useDesignModel(storageKey = "erc:design-model") {
  const { data: models = [] } = useQuery<DesignModel[]>({
    queryKey: ["design-models"],
    queryFn: () => apiClient.getDesignModels(),
    staleTime: 5 * 60_000,
  });

  const [model, setModelState] = useState<string>(() => {
    try {
      return localStorage.getItem(storageKey) || "";
    } catch {
      return "";
    }
  });

  // Kayıtlı model yoksa/geçersizse varsayılan GPT Image 2.5 Sunburst (yoksa ilk model) —
  // bunu persist etme, sadece kullanıcı elle seçtiğinde kaydet. Eski ortak seçim
  // devralınmıyor: başka ekranda seçilen pahalı/yanlış model yeni ekrana taşınıyordu.
  const fallback = models.find((m) => m.id === DEFAULT_IMAGE_MODEL)?.id ?? models[0]?.id ?? "";
  useEffect(() => {
    if (!models.length) return;
    if (!model || !models.some((m) => m.id === model)) setModelState(fallback);
  }, [models, model]);

  const setModel = (id: string) => {
    setModelState(id);
    try {
      localStorage.setItem(storageKey, id);
    } catch {
      /* ignore */
    }
  };

  const effectiveModel = model || fallback;
  return { models, model: effectiveModel, setModel };
}
